import React, { useState } from 'react';
import { AlertNotification, LabeledRadioInButton, Loading, Text, fontFamilies } from '@commonsku/styles';
import useCompanyGeneralData from './hooks/useCompanyGeneralData';
import { useHasUserFlag } from '../../hooks';
import ErrorBoundary from '../../components/ErrorBoundary';
import AdminStripe from './AdminStripe';
import AdminCardConnect from './AdminCardConnect';
import AdminForward from './AdminForward';
import ConfirmPopup from '../ConfirmPopup';
import CREDIT_CARD_PROVIDERS from '../../constants/CreditCardProviders';

export const FLAG_COMMONSKU_PAYMENTS = 'COMMONSKU-PAYMENTS';
export const FLAG_COMMONSKU_PAYMENTS_QBO_REFUNDS_UI = 'COMMONSKU-PAYMENTS-QBO-REFUNDS-UI';

// COMMONSKU_PAYMENTS is saved in the database as CSKUPAYMENTS
export const CREDIT_CARD_PROVIDER_DB_VALUE = {
    ...CREDIT_CARD_PROVIDERS,
    [CREDIT_CARD_PROVIDERS.COMMONSKU_PAYMENTS.value]: {
        label: CREDIT_CARD_PROVIDERS.COMMONSKU_PAYMENTS.label,
        value: CREDIT_CARD_PROVIDERS.COMMONSKU_PAYMENTS.value,
    },
};
export const CREDIT_CARD_STATUS = {
    PENDING: 'Pending',
    PAID: 'Paid',
    FAILED: 'Failed',
    REFUND_PENDING: 'Refund pending',
    REFUND_FAILED: 'Refund failed',
    REFUNDED: 'Refunded',
    VOIDED: 'Voided',
    NONE: '(None)',
    NO_DATA: '(No data)',
};

export const REFUNDS_TAB_STATUSES = ['REFUND_PENDING', 'REFUNDED', 'REFUND_FAILED', 'VOIDED'];

const bodyTextStyle = {
    fontFamily: fontFamilies.skuFontRegular,
    fontSize: 14,
    lineHeight: '24px',
};

const alertTextStyle = {
    fontFamily: fontFamilies.skuFontRegular,
    fontSize: 16,
    lineHeight: '24px',
};

export default function CreditCardIntegration({ openCommonskuPayments = false }) {
    const { data: { data }, loading, saveExtra } = useCompanyGeneralData();
    const hasCommonskuPaymentsBeta = useHasUserFlag(FLAG_COMMONSKU_PAYMENTS);
    const [switchAwayTarget, setSwitchAwayTarget] = useState(null);

    const software = data?.companyData?.credit_card_software;
    // decouples the integration and stored credit card info
    const selectedIntegration = data?.companyData?.commonsku_payments
        ? CREDIT_CARD_PROVIDERS.COMMONSKU_PAYMENTS.value
        : (software || null);

    const handleChangeExtraData = (field, value) => {
        saveExtra({
            [field]: value
        });
    };

    const handleCreditCardSoftwareChange = (value) => {
        if (selectedIntegration === CREDIT_CARD_PROVIDERS.COMMONSKU_PAYMENTS.value) {
            setSwitchAwayTarget(value);
            return;
        }

        handleChangeExtraData('credit_card_software', value);
    };

    const handleSwitchAwayConfirm = () => {
        if (switchAwayTarget === null) {
            return;
        }

        const target = switchAwayTarget;
        setSwitchAwayTarget(null);
        handleChangeExtraData('credit_card_software', target);
    };

    if (loading !== 'idle') return <Loading />;

    const targetProvider = switchAwayTarget !== null
        ? Object.values(CREDIT_CARD_PROVIDERS).find((p) => p.value === switchAwayTarget)
        : null;
    const providerLabel = targetProvider?.label ?? null;
    const switchAwayTitle = providerLabel ? `Switch to ${providerLabel}` : null;

    return (
        <ErrorBoundary>
            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                {hasCommonskuPaymentsBeta && (
                    <LabeledRadioInButton
                        flexGrow
                        label={CREDIT_CARD_PROVIDERS.COMMONSKU_PAYMENTS.label}
                        checked={selectedIntegration === CREDIT_CARD_PROVIDERS.COMMONSKU_PAYMENTS.value}
                        onChange={(e) => { handleChangeExtraData('commonsku_payments', 1); }}
                    />
                )}
                <LabeledRadioInButton flexGrow label={CREDIT_CARD_PROVIDERS.CARDCONNECT.label} checked={selectedIntegration === CREDIT_CARD_PROVIDERS.CARDCONNECT.value} onChange={(e) => { handleCreditCardSoftwareChange(CREDIT_CARD_PROVIDERS.CARDCONNECT.value); }} />
                <LabeledRadioInButton flexGrow label={CREDIT_CARD_PROVIDERS.STRIPE.label} checked={selectedIntegration === CREDIT_CARD_PROVIDERS.STRIPE.value} onChange={(e) => { handleCreditCardSoftwareChange(CREDIT_CARD_PROVIDERS.STRIPE.value); }} />
                <LabeledRadioInButton flexGrow label={CREDIT_CARD_PROVIDERS.NONE.label} checked={!selectedIntegration} onChange={(e) => { handleCreditCardSoftwareChange(CREDIT_CARD_PROVIDERS.NONE.value); }} />
            </div>

            <div style={{ padding: '1em 0' }}>
                {selectedIntegration === CREDIT_CARD_PROVIDERS.CARDCONNECT.value && <AdminCardConnect />}
                {selectedIntegration === CREDIT_CARD_PROVIDERS.STRIPE.value && <AdminStripe />}
                {((selectedIntegration === CREDIT_CARD_PROVIDERS.COMMONSKU_PAYMENTS.value) || openCommonskuPayments) && <AdminForward />}
            </div>

            {switchAwayTarget !== null && (
                <ConfirmPopup
                    title={switchAwayTitle}
                    cancelLabel="Cancel"
                    confirmLabel={switchAwayTitle}
                    style={{ width: 400 }}
                    showCloseIcon
                    onClose={() => setSwitchAwayTarget(null)}
                    onConfirm={handleSwitchAwayConfirm}
                >
                    {switchAwayTarget === CREDIT_CARD_PROVIDERS.NONE.value ? (
                        <Text style={bodyTextStyle}>
                            You will no longer have an active payment provider. New invoices and shop
                            checkouts will not process credit card payments.
                        </Text>
                    ) : (
                        <Text style={bodyTextStyle}>
                            <strong>{providerLabel}</strong>
                            {' will become your active payment provider immediately. New invoices and shop checkouts will be processed through '}
                            <strong>{providerLabel}</strong>
                            .
                        </Text>
                    )}
                    <AlertNotification alertType="warn">
                        <Text color="secondary2" style={alertTextStyle}>
                            Mixing payment methods across ongoing projects may require additional
                            reconciliation effort.
                        </Text>
                    </AlertNotification>
                </ConfirmPopup>
            )}
        </ErrorBoundary>
    );
}
